import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from 'react-toastify';
import '../../App.css'
import { Utils, fetchWithAuth, bigAmountFormat, getDefaultPageSize, getTotalPages, getPlanDisplayName, PLAN_TYPE_CODES, can, exportListCsv } from '../../utils';
import SkeletonLoader from "../common/SkeletonLoader";
import { useNavigate } from "react-router";
import { Modal } from "react-bootstrap";
import { MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { BsPencil } from "react-icons/bs";
import { RxCross2 } from "react-icons/rx";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";
import Swal from "sweetalert2";
const Organizations = () => {
    const [userData] = useState(() => JSON.parse(localStorage.getItem('userData')));
    const [plan, setPlan] = useState("")
    const [planType, setPlanType] = useState(null)
    const [appList, setAppList] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [isExporting, setIsExporting] = useState(false);
    const tableInnerRef = useRef(null);
    const tableContainerRef = useRef(null);
    const renamingOrgRef = useRef(null);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [savingRename, setSavingRename] = useState(false);
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = getDefaultPageSize();
    const [orgTotal, setOrgTotal] = useState(0);

    const getOrgActionOptionCount = () =>
      1 + (can("apps.edit") ? 1 : 0) + (can("apps.delete") ? 1 : 0);
    const { openActionItem, dropdownPosition, dropdownRef, closeDropdown, handleOpenDropdown } = useActionDropdown({
      tableScrollRef: tableContainerRef,
      optionCount: getOrgActionOptionCount,
      menuWidth: 220,
    });
    const isAtScrollEnd = useScrollEndDetection(tableContainerRef, tableInnerRef, appList?.length);
    const tableMaxHeight = useTableMaxHeight(tableContainerRef);

    const openRename = (org) => {
        closeDropdown();
        renamingOrgRef.current = org;
        setRenameValue(org?.oganization_name || "");
        setShowRenameModal(true);
    };

    const goToOrgApps = (org) => {
        closeDropdown();
        navigate("/apps", { state: { orgId: org?.id } });
    };

    const handleRenameSubmit = async () => {
        const name = String(renameValue || "").trim();
        if (!renamingOrgRef.current?.id) return;
        if (!name) {
            toast.error("Organization name is required");
            return;
        }
        setSavingRename(true);
        try {
            const res = await fetch(Utils.baseurl + "update-organization", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ organizationId: renamingOrgRef.current.id, userId: userData.id, oganization_name: name }),
            });
            const result = await res.json();
            if (result.status) {
                toast.success(result.message || "Organization updated");
                setShowRenameModal(false);
                fetchOrganizations();
            } else {
                toast.error(result.message || "Failed to update organization");
            }
        } catch (e) {
            toast.error("Network error");
        } finally {
            setSavingRename(false);
        }
    };

    const handleRemove = async (org) => {
        closeDropdown();
        if (!org?.id) return;
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: "Remove this organization?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove",
        });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch(Utils.baseurl + "delete-organization", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ organizationId: org.id, userId: userData.id }),
            });
            const result = await res.json();
            if (result.status) {
                toast.success(result.message || "Organization removed");
                fetchOrganizations();
            } else {
                toast.error(result.message || "Failed to remove organization");
            }
        } catch (e) {
            toast.error("Network error");
        }
    };

    const fetchPlan = useCallback(() => {
        fetch(Utils.baseurl + 'user/getbyid', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: userData.id }),
        })
            .then((r) => r.json())
            .then((result) => {
                if (result.status && result.data?.sub) {
                    const subPlanType = result.data.sub.plan_type;
                    setPlanType(subPlanType ?? null);
                    setPlan(subPlanType ? getPlanDisplayName(subPlanType) : "Free");
                }
            })
            .catch(() => { /* ignore */ });
    }, [userData.id]);

    const fetchOrganizations = useCallback(() => {
        setIsLoading(true);
        fetchWithAuth(Utils.baseurl + 'get-organizations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: currentPage, pageSize }),
        })
            .then((response) => response.json())
            .then((result) => {
                if (result.status) {
                    setAppList(result.data || []);
                    setOrgTotal(result.meta?.total ?? (result.data?.length || 0));
                } else {
                    toast.error(result.message || 'Failed to fetch organizations');
                    setAppList([]);
                    setOrgTotal(0);
                }
            })
            .catch(() => {
                toast.error('Failed to fetch organizations');
                setAppList([]);
                setOrgTotal(0);
            })
            .finally(() => setIsLoading(false));
            // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData.id, currentPage, pageSize]);

    useEffect(() => {
        fetchPlan();
    }, [fetchPlan]);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    useEffect(() => {
        setCurrentPage(1);
    }, [orgTotal]);

    const canRemoveOrg = (org) => {
        const hasApps = (org?.applications?.length || 0) > 0;
        const isEnterprise = Number(planType) === PLAN_TYPE_CODES[2].value;
        return !isEnterprise && !hasApps;
    };

    const removeDisabledReason =
        "An organization can only be removed if it is not on an enterprise plan and all of the applications within the organization have been removed.";

    const totalRows = orgTotal || 0;
    const totalPages = getTotalPages(totalRows, pageSize);
    const safePage = Math.min(Math.max(1, currentPage), totalPages);


    let loaderData = [
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        }
    ]

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { status, message } = await exportListCsv("get-organizations", {}, "organizations.csv");
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <div className="dashboard_right">
                <div className="dashboard_inner">
                    <div className="dashboard_right_top">
                        <div className="dashboard_heading">
                            <h1>Organizations</h1>
                        </div>
                        <div className="add_app" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={fetchOrganizations}>
                                <FiRefreshCw />
                                <span>Refresh</span>
                            </button>
                            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
                                <MdFileDownload />
                                <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                            </button>
                        </div>
                    </div>
                    <div className="dashboard_app_table dashboard_app_table--list-records">

                        <div className="dashboard_table">
                            <div ref={tableContainerRef} className="table_scroll_wrapper">
                                <div
                                    ref={tableInnerRef}
                                    className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                                    style={{
                                        maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                                        overflowY: 'auto',
                                        overflowX: 'auto',
                                    }}
                                >
                                    <table className="table_outer table-sm">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Plan</th>
                                                <th style={{ textAlign: "center" }}>Mobile (MAU)</th>
                                                <th style={{ textAlign: "center" }}>Web Push Subscribers</th>
                                                <th style={{ textAlign: "center" }}>Total Records</th>
                                                <th style={{ textAlign: "center" }}>Total Subscribed</th>
                                                <th style={{ textAlign: "center" }}>Apps</th>
                                                <th className="col-action" style={{ textAlign: "right" }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {appList.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" style={{ textAlign: "center" }}>
                                                        {
                                                            isLoading ?
                                                                // <FontAwesomeIcon icon={faSpinner} style={{ marginTop: "10px" }} spin size="2x" />
                                                                <SkeletonLoader columns={loaderData} rows={2} />
                                                                : <div style={{ padding: "40px 20px" }}>
                                                                    <p style={{ fontSize: 20, fontWeight: 500, marginBottom: "10px" }} >No organizations found</p>
                                                                    <p style={{ fontSize: 14, color: "#666", marginBottom: "20px" }}>Create your first organization to get started</p>
                                                                    {can("apps.add") ? (
                                                                    <a href="/organizations/new" style={{
                                                                        display: "inline-block",
                                                                        padding: "10px 20px",
                                                                        background: "#4F46E5",
                                                                        color: "white",
                                                                        borderRadius: "6px",
                                                                        textDecoration: "none",
                                                                        fontWeight: 500
                                                                    }}>
                                                                        Create Organization
                                                                    </a>
                                                                    ) : null}
                                                                </div>
                                                        }
                                                    </td>
                                                </tr>
                                            ) : appList.map(item => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <button type="button" onClick={() => goToOrgApps(item)}>{item.oganization_name}</button>
                                                    </td>
                                                    <td className="plan"><span>{plan}</span></td>
                                                    <td style={{ textAlign: "center" }}>{bigAmountFormat(item.mau_count ?? 0)}</td>
                                                    <td style={{ textAlign: "center" }}>{bigAmountFormat(item.web_push_subscribers_count ?? 0)}</td>
                                                    <td style={{ textAlign: "center" }}>{bigAmountFormat(item.app_users_count ?? 0)}</td>
                                                    <td style={{ textAlign: "center" }}>{bigAmountFormat(item.subscriptions_active_count ?? 0)}</td>
                                                    <td style={{ textAlign: "center" }}>{item.applications?.length || 0}</td>
                                                    <td className="col-action" style={{ textAlign: "right" }}>
                                                        <button
                                                            type="button"
                                                            className="dropdown-trigger-btn"
                                                            onClick={(e) => handleOpenDropdown(e, item.id, item)}
                                                        >
                                                            <svg viewBox="0 0 16 16" fill="currentColor">
                                                                <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" fill="currentColor">
                                                                </path>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            <ListPaginationBar
                                page={safePage}
                                setPage={setCurrentPage}
                                total={totalRows}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                ariaLabel="Organizations pagination"
                                loading={isLoading}
                            />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {openActionItem && dropdownPosition && createPortal(
                <div ref={dropdownRef} style={{ position: 'fixed', left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 10000, minWidth: '220px' }}>
                    <div className="dropdown-menu-subscription">
                        <button
                            type="button"
                            onClick={() => goToOrgApps(openActionItem)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            <IoEye size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>View Organization</span>
                        </button>
                        {can("apps.edit") ? (
                        <button
                            type="button"
                            onClick={() => openRename(openActionItem)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            <BsPencil size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Rename</span>
                        </button>
                        ) : null}
                        {can("apps.delete") ? (
                        <button
                            type="button"
                            className="delete-class"
                            onClick={() => canRemoveOrg(openActionItem) && handleRemove(openActionItem)}
                            disabled={!canRemoveOrg(openActionItem)}
                            title={!canRemoveOrg(openActionItem) ? removeDisabledReason : undefined}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                color: 'red',
                                opacity: !canRemoveOrg(openActionItem) ? 0.6 : 1,
                                cursor: !canRemoveOrg(openActionItem) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <RxCross2 size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Remove</span>
                        </button>
                        ) : null}
                    </div>
                </div>,
                document.body
            )}

            {/* Rename Modal */}
            <Modal show={showRenameModal} onHide={() => !savingRename && setShowRenameModal(false)} centered className="ns-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Rename Organization</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form_group">
                        <label>New Organization Name</label>
                        <input
                            type="text"
                            className="form_control"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn_theme_secondary" onClick={() => !savingRename && setShowRenameModal(false)}>
                        Cancel
                    </button>
                    <button type="button" className="add_app_btn btn_theme_primary" onClick={handleRenameSubmit} disabled={savingRename}>
                        <span>{savingRename ? "Saving…" : "Rename"}</span>
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Organizations;
